import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TableContainer,
  Chip,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import SearchField from "react-search-field";
// import Option from "./Option";

const options = [
  { id: 1, name: "Lab Name", value: "name" },
];

const statusStyles = {
  Creating: { backgroundColor: "#1976d2", label: "InActive" },
  Scheduled: { backgroundColor: "rgb(44 189 186)", label: "InActive" },
  Running: { backgroundColor: "#00a200", label: "Active" },
  Active: { backgroundColor: "#00a200", label: "Active" },
  Stopped: { backgroundColor: "rgb(228 193 3 / 94%)", label: "InActive" },
  Cordoned: { backgroundColor: "rgb(98 95 80 / 94%)", label: "InActive" },
  default: { backgroundColor: "rgb(228 3 3 / 94%)", label: "InActive" }
};

const LabList = ({ ...rest }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(true);
  const [instancelist, setInstanceList] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");

  // Memoized filtered data
  const filteredData = useMemo(() => {
    return instancelist.filter(val => {
      if (!searchTerm) return true;
      return val[options[0].value].toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [instancelist, searchTerm]);

  // Paginated data
  const paginatedData = useMemo(() => {
    return filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  // // Format date time
  //  const DateTime1 = (startDate, isCloud) => {
  //   if (isCloud) return startDate;

  //   const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  //   const startDate1 = new Date(startDate).toLocaleString("en-GB", {
  //     timeZone: `${timezone}`,
  //   });
  //   const startDate2 = startDate1.split(",");
  //   const startDatef = startDate2[0].replace("/", "-");
  //   const startDate3 = startDatef.replace("/", "-");

  //   return startDate3;
  //  };
  
  function DateTime1(endDate, endTime, isCloud) {
    if (isCloud) return endDate;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const endDate1 = new Date(endDate + "T" + endTime + ":00.000Z").toLocaleString("en-GB", { timeZone: `${timezone}` });
    const endDate2 = endDate1.split(",");
    const endDatef = endDate2[0].replace("/", "-");
    const endDate3 = endDatef.replace("/", "-");
    return endDate3;
  }

  function DateTime3(startDate, startTime) {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const startTime1 = new Date(startDate + "T" + startTime + ":00.000Z").toLocaleString("en-GB", { timeZone: `${timezone}` });
    const startTime2 = startTime1.split(",");
    const startTime3 = startTime2[1].substring(0, 6);
    return startTime3;
  }

  // Fetch data
  useEffect(() => {
    let isInitialLoad = true;
    
    const fetchData = async () => {
      try {
        if (isInitialLoad) {
          setLoading(true);
        }
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/traineeoveralllablist/`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setInstanceList(data);
        }
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        if (isInitialLoad) {
          setLoading(false);
          isInitialLoad = false;
        }
      }
    };
  
    fetchData();
    const intervalId = setInterval(fetchData, 2000);
    
    return () => clearInterval(intervalId);
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getDetailPath = (type) => {
    const paths = {
      preconfig: "/app/preconfig/detail",
      custom: "/app/custom/detail",
      hvm: "/app/hypervised/detail",
      svm: "/app/shared/detail",
      cloud: "/app/cloud/detail",
      wise: "/app/wise/detail",
      manual: "/app/manual/detail",
      saas: "/app/saas/detail"
    };
    return paths[type] || "#";
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 3 }}>
      <Box sx={{ 
        display: "flex", 
        justifyContent: "flex-end", 
        alignItems: "center", 
        mb: 2,
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? 2 : 0
      }}>
        <SearchField
          placeholder="Search by Lab Name"
          onChange={(value) => setSearchTerm(value)}
          classNames="test-class"
        />
      </Box>

      <Card {...rest}>
        <TableContainer>
          <Table size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell>Lab Name</TableCell>
                <TableCell>Status</TableCell>
                {!isMobile && <TableCell>Technology</TableCell>}
                <TableCell>Start Date</TableCell>
                <TableCell>Start Duration</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Start Duration</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            
            <TableBody>
              {paginatedData.length > 0 ? (
                paginatedData.map((instance) => (
                  <TableRow hover key={instance.id}>
                    <TableCell>{instance.name}</TableCell>
                    <TableCell>
                      <Chip
                        color="primary"
                        style={{
                          backgroundColor: statusStyles[instance.status]?.backgroundColor || statusStyles.default.backgroundColor,
                          color: "white",
                          minWidth: 80
                        }}
                        label={statusStyles[instance.status]?.label || statusStyles.default.label}
                        size="small"
                      />
                    </TableCell>
                    
                    {!isMobile && <TableCell>{instance.application}</TableCell>}
                    
                    <TableCell>
                      {DateTime1(instance.start_date, instance.start_time, instance.type === "cloud")}
                    </TableCell>

                    <TableCell>
                      <span style={{ fontWeight: 'bold' }}>Start Time: </span>
                      {DateTime3(instance.start_date, instance.start_time, instance.type === "cloud")}
                    </TableCell>
                    
                    <TableCell>
                      {DateTime1(instance.end_date, instance.stop_time, instance.type === "cloud")}
                    </TableCell>

                    <TableCell>
                      <span style={{ fontWeight: 'bold' }}>End Time: </span>
                       {DateTime3(instance.end_date, instance.stop_time)}
                    </TableCell>

                    <TableCell>
                      <Button 
                        size="small" 
                        component={Link}
                        to={getDetailPath(instance.type)}
                        onClick={() => localStorage.setItem("InstanceID", instance.id)}
                      >
                        <ArrowRightAltIcon color="primary" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={isMobile ? 5 : 7} align="center">
                    <Typography variant="body1">No Data Available</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredData.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', paddingLeft: '20px' }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default LabList;